import { AuthExpired, createOfflineDb, createOutbox, createReadCache } from '@dowty/offline';
import { isNetworkError } from '@dowty/core';
import { StatusCode } from 'grpc-web';
import type { AuthUser } from '../context/AuthContext';
import { SSO_ENABLED } from '../grpcConfig';
import { addDrinkActivity } from '../services/drinks/DrinkService';

export type ReadCacheKey = 'drinks' | `activities:${number}` | 'beerpongRules';

/** Stored shape of a queued drink. The token is deliberately absent: `send` reads it when the entry goes out. */
interface PendingDrinkPayload {
  drink: { drinkid: number; drinkname: string; promille: number };
  amount: number;
  /** Epoch ms, set client-side when the drink was logged so a late replay still lands at the right point in the history. */
  tsInsert: number;
  tsStart?: number;
  eventId?: number;
  user: { userid: number; username: string };
}

// Existing database name and cache prefix, so entries queued by earlier builds are still sent.
export const offlineDb = createOfflineDb('saufi-offline');
export const reads = createReadCache<ReadCacheKey, any>(offlineDb);

let currentUser: AuthUser | null = null;

export const setOutboxUser = (user: AuthUser | null): void => {
  currentUser = user;
};

export const outbox = createOutbox<PendingDrinkPayload>(offlineDb, {
  isNetworkError,
  send: async ({ drink, amount, tsInsert, tsStart, eventId, user }, idempotencyKey) => {
    const current = currentUser;
    // Another account (or none) is signed in: wait until the owner of the entry is back.
    if (!current || current.userId !== user.userid || (!current.token && !SSO_ENABLED)) throw new AuthExpired();
    try {
      return await addDrinkActivity(
        current,
        { id: drink.drinkid, name: drink.drinkname, promille: drink.promille },
        amount,
        new Date(tsInsert),
        { startedAt: tsStart === undefined ? null : new Date(tsStart), eventId, idempotencyKey }
      );
    } catch (error) {
      if ((error as { code?: StatusCode }).code === StatusCode.UNAUTHENTICATED) throw new AuthExpired();
      throw error;
    }
  },
});
